"use server";

import { unstable_cache } from "next/cache";
import type { ProductType } from "@/api_server";
import { getGraphQlClient } from "@/graphql/client";
import type {
  FooterQueryVariables,
  Locales,
  ProductEntryPageQueryVariables,
  ProductPricePageQuery,
  ProductPricePageQueryVariables,
  ProductTopQuestionsPageQuery,
  ProductTopQuestionsPageQueryVariables,
  TopMenuContentQueryVariables,
} from "@/graphql/generated";
import {
  FooterDocument,
  ProductEntryPageDocument,
  ProductPricePageDocument,
  ProductTopQuestionsPageDocument,
  TopMenuContentDocument,
} from "@/graphql/generated";
import type { FooterContent } from "@/services/cms/contentTypes/navigation/FooterContent";
import type { TopMenuContent } from "@/services/cms/contentTypes/navigation/TopMenuContent";
import { mapEntryContentByProduct } from "./contentMappers/entryMapper";
import { mapFooterContent } from "./contentMappers/footerMapper";
import { mapTopMenuContent } from "./contentMappers/topMenuMapper";
import type { EntryContentByProductType } from "./contentTypes/entry/generalEntryTypes";

export async function getTopMenuContent(language: Locales | string) {
  const getCachedTopMenuContent = unstable_cache(
    async (): Promise<TopMenuContent> => {
      const parsedLanguage = (language as Locales) ?? "fi";
      const graphQlClient = getGraphQlClient();
      const variables: TopMenuContentQueryVariables = {
        locale: parsedLanguage,
        urlPattern: `%topmenu%`,
      };

      const topMenuContent = await graphQlClient.request(
        TopMenuContentDocument,
        variables,
      );

      return mapTopMenuContent(topMenuContent);
    },
    ["top-menu-content", language],
    {
      revalidate: 180,
    },
  );

  return getCachedTopMenuContent();
}

export async function getProductEntryContent<P extends ProductType>(
  product: P,
  language: Locales,
): Promise<EntryContentByProductType<P>> {
  const getCachedEntryContent = unstable_cache(
    async (): Promise<EntryContentByProductType<P>> => {
      const graphQlClient = getGraphQlClient();
      const variables: ProductEntryPageQueryVariables = {
        locale: language,
        urlPattern: `%entry/${product}%`,
      };

      const entryContent = await graphQlClient.request(
        ProductEntryPageDocument,
        variables,
      );

      return mapEntryContentByProduct(product, entryContent);
    },
    ["entry-page-content", product, language],
    {
      revalidate: 180,
    },
  );

  return getCachedEntryContent();
}

export async function getProductTopQuestionsContent(
  product: ProductType,
  language: Locales,
): Promise<ProductTopQuestionsPageQuery> {
  const getCachedTopQuestionsContent = unstable_cache(
    async (): Promise<ProductTopQuestionsPageQuery> => {
      const graphQlClient = getGraphQlClient();
      const variables: ProductTopQuestionsPageQueryVariables = {
        locale: language,
        urlPattern: `%topquestions/${product}%`,
      };

      return graphQlClient.request(ProductTopQuestionsPageDocument, variables);
    },
    ["top-questions-page-content", product, language],
    {
      revalidate: 180,
    },
  );

  return getCachedTopQuestionsContent();
}

export async function getProductPriceContent(
  product: ProductType,
  language: Locales,
): Promise<ProductPricePageQuery> {
  const getCachedPriceContent = unstable_cache(
    async (): Promise<ProductPricePageQuery> => {
      const graphQlClient = getGraphQlClient();
      const variables: ProductPricePageQueryVariables = {
        locale: language,
        urlPattern: `%price/${product}%`,
      };

      return graphQlClient.request(ProductPricePageDocument, variables);
    },
    ["price-page-content", product, language],
    {
      revalidate: 180,
    },
  );

  return getCachedPriceContent();
}

export async function getFooterContent(language: Locales | string) {
  const getCachedFooterContent = unstable_cache(
    async (): Promise<FooterContent> => {
      const parsedLanguage = (language as Locales) ?? "fi";
      const graphQlClient = getGraphQlClient();
      const variables: FooterQueryVariables = {
        locale: parsedLanguage,
        urlPattern: `%footer-common%`,
      };

      const footerContent = await graphQlClient.request(
        FooterDocument,
        variables,
      );
      return mapFooterContent(footerContent);
    },
    ["footer-content", language],
    {
      revalidate: 180,
    },
  );

  return getCachedFooterContent();
}
